"use client";

import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";

import { ChevronRightIcon } from "../../../_components/dx-icons";
import { useHorizontalDragGuard } from "../../../_components/main-visual/_hooks/use-horizontal-drag-guard";

import { DxIconButton } from "./dx-icon-button";
import { DxImageViewer } from "./dx-image-viewer";

type ImageWithCaption = { url: string; caption?: string | null };

type Props = {
  images: ImageWithCaption[] | undefined;
  altText: string;
};

const THUMB_SIZE_ACTIVE = 60;
const THUMB_SIZE_INACTIVE = 56;
const THUMB_GAP = 4;
const PLACEHOLDER_COUNT = 9;
const LG_MIN_WIDTH = 1024;

/** DXPJ ページ向けメインビジュアル + サムネ列 (画像 caption + SP 全画面モーダル付き)。 */
export function DxMainVisual({ images, altText }: Props) {
  const items: Array<ImageWithCaption | null> =
    images && images.length > 0
      ? images
      : Array.from({ length: PLACEHOLDER_COUNT }, () => null);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    dragFree: false,
    containScroll: "trimSnaps",
    watchDrag: true,
  });
  const [thumbEmblaRef, thumbEmblaApi] = useEmblaCarousel({
    dragFree: true,
    containScroll: "trimSnaps",
    watchDrag: true,
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const handleSelect = () => setCurrentIndex(emblaApi.selectedScrollSnap());
    handleSelect();
    emblaApi.on("select", handleSelect);
    emblaApi.on("reInit", handleSelect);
    return () => {
      emblaApi.off("select", handleSelect);
      emblaApi.off("reInit", handleSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!thumbEmblaApi) return;
    if (thumbEmblaApi.slidesInView().includes(currentIndex)) return;
    thumbEmblaApi.scrollTo(currentIndex);
  }, [thumbEmblaApi, currentIndex]);

  const heroDragGuard = useHorizontalDragGuard();
  const thumbDragGuard = useHorizontalDragGuard();

  const scrollHeroTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  // サムネ列の送り矢印 (PC hover)。 dragFree なので scroll でも可否を更新する。
  const [thumbCanPrev, setThumbCanPrev] = useState(false);
  const [thumbCanNext, setThumbCanNext] = useState(false);
  useEffect(() => {
    if (!thumbEmblaApi) return;
    const update = () => {
      setThumbCanPrev(thumbEmblaApi.canScrollPrev());
      setThumbCanNext(thumbEmblaApi.canScrollNext());
    };
    update();
    thumbEmblaApi
      .on("reInit", update)
      .on("select", update)
      .on("scroll", update);
    return () => {
      thumbEmblaApi
        .off("reInit", update)
        .off("select", update)
        .off("scroll", update);
    };
  }, [thumbEmblaApi]);
  const thumbScrollPrev = useCallback(
    () => thumbEmblaApi?.scrollPrev(),
    [thumbEmblaApi],
  );
  const thumbScrollNext = useCallback(
    () => thumbEmblaApi?.scrollNext(),
    [thumbEmblaApi],
  );

  // lg+ は sidebar 表示で画面に余裕があるので拡大不要、 SP のみ全画面モーダル。
  const openModalIfMobile = useCallback(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth >= LG_MIN_WIDTH) return;
    setModalOpen(true);
  }, []);

  const currentItem = items[currentIndex];
  const currentCaption = currentItem?.caption ?? null;
  const hasCurrentCaption = currentCaption !== null && currentCaption !== "";

  return (
    <>
      <div className="relative">
        <div
          ref={emblaRef}
          className="bg-dx-surface-subtle aspect-[3/2] w-full overflow-hidden"
          {...heroDragGuard}
        >
          <div className="flex h-full">
            {items.map((item, i) => (
              <div
                key={i}
                // lg+ は何もしないので preflight の cursor:pointer を打ち消す。
                className="relative h-full w-full shrink-0 grow-0 basis-full cursor-zoom-in select-none lg:cursor-default"
                onClick={openModalIfMobile}
                // 画像の拡大表示だけで、行動の分析対象にならない
                data-analytics-ignore
                role={item?.url ? "button" : undefined}
                tabIndex={item?.url ? -1 : undefined}
                aria-label={item?.url ? "画像を拡大表示" : undefined}
              >
                {item?.url && (
                  <Image
                    src={item.url}
                    alt={`${altText}（${i + 1}枚目）`}
                    fill
                    className="pointer-events-none object-cover object-center"
                    // 1024+ で hero 実幅が 1016px 近くまで広がる。 sizes 過小だと Next/Image が低解像 variant を出して画質劣化。
                    sizes="(min-width: 1024px) 1080px, 100vw"
                    quality={90}
                    draggable={false}
                    priority={i === 0}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 画像の上に重ねるだけで、 タップは下の拡大表示へ透過させる。 */}
        {hasCurrentCaption && (
          <div className="pointer-events-none absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/60 to-transparent px-[16px] pt-[24px] pb-[16px]">
            <p className="font-notojp text-dx-base-white text-[14px] leading-[1.6]">
              {currentCaption}
            </p>
          </div>
        )}
      </div>

      <div className="group/thumbs relative">
        <div
          ref={thumbEmblaRef}
          className="overflow-hidden"
          style={{ height: THUMB_SIZE_ACTIVE + 4 }}
          {...thumbDragGuard}
        >
          <div
            className="flex h-full items-center"
            style={{ gap: THUMB_GAP, paddingBlock: 2 }}
          >
            {items.map((item, i) => {
              const isActive = i === currentIndex;
              const size = isActive ? THUMB_SIZE_ACTIVE : THUMB_SIZE_INACTIVE;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => scrollHeroTo(i)}
                  // 表示する画像を切り替えるだけの表示制御
                  data-analytics-ignore
                  className="group/thumb bg-dx-surface-subtle relative shrink-0 cursor-pointer overflow-hidden transition-[width,height] duration-150"
                  style={{ width: size, height: size }}
                  aria-label={`画像 ${i + 1} を表示`}
                  aria-current={isActive}
                >
                  {item?.url && (
                    <Image
                      src={item.url}
                      alt=""
                      fill
                      className="pointer-events-none object-cover"
                      sizes="200px"
                      loading="lazy"
                      draggable={false}
                    />
                  )}
                  {/* 非アクティブは dim overlay、 hover で解除 (Figma ImageViewThumbsItem)。 */}
                  {item?.url && !isActive && (
                    <span className="pointer-events-none absolute inset-0 bg-[rgba(106,102,92,0.32)] transition-opacity duration-150 group-hover/thumb:opacity-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {/* PC hover でサムネ列の左右送り矢印 (DXPJ-62 3lfhP7B)。 SP は swipe。 */}
        <div className="xs:flex pointer-events-none absolute inset-y-0 left-0 hidden items-center">
          {thumbCanPrev && (
            <div className="pointer-events-auto opacity-0 transition-opacity duration-150 group-hover/thumbs:opacity-100">
              <DxIconButton
                variant="float"
                size="xsmall"
                ariaLabel="前のサムネイル"
                // カルーセルの送りだけで、行動の分析対象にならない
                data-analytics-ignore
                onClick={thumbScrollPrev}
                icon={<ChevronRightIcon className="rotate-180" />}
              />
            </div>
          )}
        </div>
        <div className="xs:flex pointer-events-none absolute inset-y-0 right-0 hidden items-center">
          {thumbCanNext && (
            <div className="pointer-events-auto opacity-0 transition-opacity duration-150 group-hover/thumbs:opacity-100">
              <DxIconButton
                variant="float"
                size="xsmall"
                ariaLabel="次のサムネイル"
                // カルーセルの送りだけで、行動の分析対象にならない
                data-analytics-ignore
                onClick={thumbScrollNext}
                icon={<ChevronRightIcon />}
              />
            </div>
          )}
        </div>
      </div>

      {modalOpen && currentItem?.url && (
        <DxImageViewer
          imageUrl={currentItem.url}
          altText={`${altText}（${currentIndex + 1}枚目）`}
          caption={currentCaption}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}

export function DxMainVisualSkeleton({
  thumbCount = PLACEHOLDER_COUNT,
}: { thumbCount?: number } = {}) {
  return (
    <>
      <div className="bg-dx-surface-subtle aspect-[3/2] w-full animate-pulse" />
      <div
        className="flex items-center py-[2px]"
        style={{ height: THUMB_SIZE_ACTIVE + 4, gap: THUMB_GAP }}
      >
        {Array.from({ length: thumbCount }).map((_, i) => {
          const size = i === 0 ? THUMB_SIZE_ACTIVE : THUMB_SIZE_INACTIVE;
          return (
            <div
              key={i}
              className="bg-dx-surface-subtle shrink-0 animate-pulse"
              style={{ width: size, height: size }}
            />
          );
        })}
      </div>
    </>
  );
}
